import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, StatusBadge, fmtDate } from "@/components/common";
import { FedexPickupButton } from "@/components/FedexPickupDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, Trash, HandPalm, Play } from "@phosphor-icons/react";

const modeLabel = (d) =>
  d.mode === "air_cargo" ? `Air Cargo${d.airline_name ? ` · ${d.airline_name}` : ""}` : `Courier${d.courier_name ? ` · ${d.courier_name}` : ""}`;

export default function Dispatch() {
  const navigate = useNavigate();
  const { isTeam } = useAuth();
  const [rows, setRows] = useState([]);
  const [search, setSearch] = useState("");
  const [open, setOpen] = useState(false);
  const [orders, setOrders] = useState([]);
  const [form, setForm] = useState({ order_ids: [], mode: "courier", courier_name: "", airline_name: "" });
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [busyDel, setBusyDel] = useState(false);
  const [holding, setHolding] = useState(null);
  const [holdReason, setHoldReason] = useState("");
  const [busyHold, setBusyHold] = useState(false);
  const toggleOrder = (oid) => setForm((f) => ({ ...f, order_ids: f.order_ids.includes(oid) ? f.order_ids.filter((x) => x !== oid) : [...f.order_ids, oid] }));

  const confirmHold = async () => {
    setBusyHold(true);
    try {
      await api.post(`/dispatches/${holding.id}/hold`, { reason: holdReason });
      toast.success(`Dispatch ${holding.dispatch_number} is ON HOLD — dispatch team alerted`);
      setHolding(null); setHoldReason(""); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to hold"); }
    finally { setBusyHold(false); }
  };
  const release = async (d) => {
    try { await api.post(`/dispatches/${d.id}/release`); toast.success(`Dispatch ${d.dispatch_number} released — dispatch team notified`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to release"); }
  };

  const confirmDelete = async () => {
    setBusyDel(true);
    try {
      await api.delete(`/dispatches/${deleting.id}`);
      toast.success(`Dispatch ${deleting.dispatch_number} deleted`);
      setDeleting(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to delete"); }
    finally { setBusyDel(false); }
  };

  const load = () => api.get("/dispatches").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => {
    load();
    if (isTeam) api.get("/orders").then((r) => setOrders(r.data.filter((o) => o.status === "approved" && !o.dispatch_id))).catch(() => {});
  }, [isTeam]);

  const createJob = async () => {
    if (form.order_ids.length === 0) return toast.error("Select at least one order");
    if (form.mode === "courier" && !form.courier_name.trim()) return toast.error("Enter the courier name");
    if (form.mode === "air_cargo" && !form.airline_name.trim()) return toast.error("Enter the airline name");
    const multi = form.order_ids.length > 1;
    setBusy(true);
    try {
      let data;
      if (multi) {
        ({ data } = await api.post("/dispatches/consolidated", { order_ids: form.order_ids, mode: form.mode, courier_name: form.courier_name, airline_name: form.airline_name }));
      } else {
        ({ data } = await api.post(`/orders/${form.order_ids[0]}/send-to-dispatch`, { mode: form.mode, courier_name: form.courier_name, airline_name: form.airline_name }));
      }
      toast.success(`Dispatch ${data.dispatch_number} created${multi ? ` (${form.order_ids.length} orders)` : ""}`);
      setOpen(false);
      navigate(`/dispatch/${data.id}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };
  const brazilOrders = orders.filter((o) => o.preferred_carrier === "brazil_courier");

  const q = search.trim().toLowerCase();
  const filtered = q
    ? rows.filter((d) => [d.awb, d.shipment_number, d.order_number, d.dispatch_number, d.ptn_name, ...(d.patients || []).map((p) => p.patient_name)].some((v) => (v || "").toLowerCase().includes(q)))
    : rows;

  return (
    <div className="space-y-6">
      <PageHeader
        subtitle="Dispatch team"
        title="Dispatch Jobs"
        actions={isTeam && <><FedexPickupButton /><Button className="rounded-sm" onClick={() => setOpen(true)} data-testid="new-dispatch-job"><Plus size={16} className="mr-2" /> New Dispatch Job</Button></>}
      />
      <div className="relative max-w-md">
        <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input className="pl-9 rounded-sm" placeholder="Search by PTN name / AWB / order / shipment…" value={search} onChange={(e) => setSearch(e.target.value)} data-testid="dispatch-search" />
      </div>
      <div className="md:hidden space-y-3" data-testid="dispatch-cards">
        {filtered.length === 0 ? (
          <p className="text-center text-muted-foreground py-10 text-sm">{q ? `No dispatch jobs match "${search}".` : "No dispatch jobs yet."}</p>
        ) : filtered.map((d) => (
          <div key={d.id} onClick={() => navigate(`/dispatch/${d.id}`)} className="hp-card p-4 space-y-2 active:bg-primary/5" data-testid={`dispatch-card-${d.dispatch_number}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <p className="font-heading font-bold">{d.dispatch_number}</p>
                <p className="text-xs text-muted-foreground truncate">{d.order_number}{d.shipment_number ? ` · ${d.shipment_number}` : ""}{d.awb ? ` · AWB ${d.awb}` : ""}</p>
              </div>
              <div className="flex flex-col items-end gap-1 shrink-0">
                <StatusBadge status={d.status} />
                <span className="text-[11px] text-muted-foreground" data-testid={`dispatch-card-date-${d.dispatch_number}`}>Sent {fmtDate(d.created_at)}</span>
                {d.status === "dispatched" && d.dispatched_at && <span className="text-[11px] text-green-700 font-medium">Dispatched {fmtDate(d.dispatched_at)}</span>}
                {d.hold?.active && <span className="rounded-full bg-red-600 text-white px-1.5 py-0.5 text-[10px] uppercase tracking-wide font-bold">On hold</span>}
              </div>
            </div>
            <div className="flex flex-wrap gap-1">
              {d.is_transit && <span className="rounded-full bg-violet-50 text-violet-700 border border-violet-200 px-1.5 py-0.5 text-[10px] uppercase font-semibold" data-testid={`dispatch-transit-badge-${d.dispatch_number}`}>Internal transit → {d.to_location}</span>}
              {d.is_brazil && <span className="rounded-full bg-teal-50 text-teal-700 border border-teal-200 px-1.5 py-0.5 text-[10px] uppercase font-semibold">Brazil</span>}
              {d.is_cool_box && <span className="rounded-full bg-sky-50 text-sky-700 border border-sky-200 px-1.5 py-0.5 text-[10px] uppercase font-semibold">Cool box</span>}
              {d.consolidated && <span className="rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 text-[10px] uppercase font-semibold">Consolidated{d.order_ids?.length ? ` ×${d.order_ids.length}` : ""}</span>}
            </div>
            <p className="text-sm"><span className="text-muted-foreground">PTN: </span>{d.ptn_name || "—"}</p>
            <p className="text-xs text-muted-foreground">{modeLabel(d)}</p>
            {isTeam && (
              <div className="flex gap-2 pt-1" onClick={(e) => e.stopPropagation()}>
                {d.status !== "dispatched" && (d.hold?.active ? (
                  <Button variant="outline" size="sm" className="rounded-sm h-10 flex-1 text-green-700 border-green-300" onClick={() => release(d)} data-testid={`dispatch-card-release-${d.dispatch_number}`}><Play size={14} className="mr-1" /> Release</Button>
                ) : (
                  <Button variant="outline" size="sm" className="rounded-sm h-10 flex-1 text-red-700 border-red-300" onClick={() => setHolding(d)} data-testid={`dispatch-card-hold-${d.dispatch_number}`}><HandPalm size={14} className="mr-1" /> Stop</Button>
                ))}
                <Button variant="outline" size="sm" className="rounded-sm h-10 w-12 text-destructive" onClick={() => setDeleting(d)} data-testid={`dispatch-card-delete-${d.dispatch_number}`}><Trash size={16} /></Button>
              </div>
            )}
          </div>
        ))}
      </div>
      <div className="hidden md:block border border-border rounded-md overflow-x-auto bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider font-semibold text-muted-foreground bg-muted/40">
              <th className="px-4 py-3 font-semibold">Dispatch #</th>
              <th className="px-4 py-3 font-semibold">Order #</th>
              <th className="px-4 py-3 font-semibold">Shipment #</th>
              <th className="px-4 py-3 font-semibold">AWB</th>
              <th className="px-4 py-3 font-semibold">PTN name</th>
              <th className="px-4 py-3 font-semibold">Method</th>
              <th className="px-4 py-3 font-semibold">Status</th>
              <th className="px-4 py-3 font-semibold">Sent to dispatch</th>
              {isTeam && <th className="px-4 py-3 font-semibold text-right">Actions</th>}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={isTeam ? 9 : 8} className="px-4 py-10 text-center text-muted-foreground" data-testid="dispatch-no-results">{q ? `No dispatch jobs match "${search}".` : "No dispatch jobs yet."}</td></tr>
            ) : filtered.map((d, i) => (
              <tr key={d.id} onClick={() => navigate(`/dispatch/${d.id}`)} className={`cursor-pointer hover:bg-primary/5 ${i % 2 ? "bg-muted/30" : ""}`} data-testid={`dispatch-row-${d.dispatch_number}`}>
                <td className="px-4 py-3 font-medium">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span>{d.dispatch_number}</span>
                    {d.is_brazil && <span className="rounded-full bg-teal-50 text-teal-700 border border-teal-200 px-1.5 py-0.5 text-[10px] uppercase tracking-wide font-semibold" data-testid={`dispatch-badge-brazil-${d.dispatch_number}`}>Brazil</span>}
                    {d.is_cool_box && <span className="rounded-full bg-sky-50 text-sky-700 border border-sky-200 px-1.5 py-0.5 text-[10px] uppercase tracking-wide font-semibold" data-testid={`dispatch-badge-coolbox-${d.dispatch_number}`}>Cool box</span>}
                    {d.hold?.active && <span className="rounded-full bg-red-600 text-white px-1.5 py-0.5 text-[10px] uppercase tracking-wide font-bold" data-testid={`dispatch-badge-hold-${d.dispatch_number}`}>On hold</span>}
                    {d.consolidated && <span className="rounded-full bg-amber-50 text-amber-700 border border-amber-200 px-1.5 py-0.5 text-[10px] uppercase tracking-wide font-semibold" data-testid={`dispatch-badge-consol-${d.dispatch_number}`}>Consolidated{d.order_ids?.length ? ` ×${d.order_ids.length}` : ""}</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{d.order_number}</td>
                <td className="px-4 py-3 text-muted-foreground">{d.shipment_number || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{d.awb || "—"}</td>
                <td className="px-4 py-3">{d.ptn_name || "—"}</td>
                <td className="px-4 py-3 text-muted-foreground">{modeLabel(d)}</td>
                <td className="px-4 py-3"><StatusBadge status={d.status} /></td>
                <td className="px-4 py-3 whitespace-nowrap" data-testid={`dispatch-date-${d.dispatch_number}`}><span className="font-medium">{fmtDate(d.created_at)}</span>{d.status === "dispatched" && d.dispatched_at && <span className="block text-[11px] text-green-700">Dispatched {fmtDate(d.dispatched_at)}</span>}</td>
                {isTeam && (
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      {d.status !== "dispatched" && (d.hold?.active ? (
                        <Button variant="outline" size="sm" className="rounded-sm h-8 text-green-700 border-green-300 hover:bg-green-50" onClick={(e) => { e.stopPropagation(); release(d); }} data-testid={`dispatch-release-${d.dispatch_number}`}>
                          <Play size={14} className="mr-1" /> Release
                        </Button>
                      ) : (
                        <Button variant="outline" size="sm" className="rounded-sm h-8 text-red-700 border-red-300 hover:bg-red-50" onClick={(e) => { e.stopPropagation(); setHolding(d); }} data-testid={`dispatch-hold-${d.dispatch_number}`}>
                          <HandPalm size={14} className="mr-1" /> Stop
                        </Button>
                      ))}
                      <Button variant="ghost" size="sm" className="rounded-sm text-destructive hover:text-destructive hover:bg-destructive/10" onClick={(e) => { e.stopPropagation(); setDeleting(d); }} data-testid={`dispatch-delete-${d.dispatch_number}`}>
                        <Trash size={16} />
                      </Button>
                    </div>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>New Dispatch Job</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-wide">Orders (approved)</Label>
              <p className="text-[11px] text-muted-foreground mb-1.5">Tick 2+ Brazil Courier orders to ship them together under one dispatch & shipment.</p>
              <div className="max-h-44 overflow-y-auto border border-border rounded-sm divide-y divide-border">
                {orders.length === 0 && <p className="px-3 py-3 text-xs text-muted-foreground">No approved orders awaiting dispatch.</p>}
                {orders.map((o) => {
                  const isBrazil = o.preferred_carrier === "brazil_courier";
                  const consolidating = form.order_ids.length > 0;
                  const selClient = form.order_ids.length ? orders.find((x) => x.id === form.order_ids[0])?.client_id : null;
                  const disabled = !form.order_ids.includes(o.id) && ((consolidating && !isBrazil) || (selClient && o.client_id !== selClient));
                  return (
                    <label key={o.id} className={`flex items-center gap-2 px-3 py-2 text-sm cursor-pointer ${disabled ? "opacity-40" : "hover:bg-primary/5"}`} data-testid={`dispatch-job-order-${o.order_number}`}>
                      <input type="checkbox" checked={form.order_ids.includes(o.id)} disabled={disabled} onChange={() => toggleOrder(o.id)} data-testid={`dispatch-job-order-cb-${o.order_number}`} />
                      <span className="flex-1">{o.order_number} — {o.client_name}</span>
                      {isBrazil && <span className="text-[10px] uppercase tracking-wide text-teal-700 font-semibold">Brazil</span>}
                    </label>
                  );
                })}
              </div>
              {form.order_ids.length > 1 && <p className="text-xs text-teal-700 mt-1.5" data-testid="dispatch-consolidate-note">Consolidating {form.order_ids.length} orders into one dispatch + shipment.</p>}
            </div>
            <div className="inline-flex rounded-md border border-border bg-muted/40 p-1" data-testid="dispatch-job-mode-tabs">
              <button type="button" onClick={() => setForm({ ...form, mode: "courier" })} data-testid="dispatch-job-mode-courier" className={`px-4 py-2 text-sm font-medium rounded-sm ${form.mode === "courier" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>Courier</button>
              <button type="button" onClick={() => setForm({ ...form, mode: "air_cargo" })} data-testid="dispatch-job-mode-air" className={`px-4 py-2 text-sm font-medium rounded-sm ${form.mode === "air_cargo" ? "bg-card text-foreground shadow-sm" : "text-muted-foreground"}`}>Air Cargo</button>
            </div>
            {form.mode === "courier" ? (
              <div><Label className="text-xs uppercase tracking-wide">Courier name</Label><Input className="mt-1.5 rounded-sm" value={form.courier_name} onChange={(e) => setForm({ ...form, courier_name: e.target.value })} data-testid="dispatch-job-courier" /></div>
            ) : (
              <div><Label className="text-xs uppercase tracking-wide">Airline name</Label><Input className="mt-1.5 rounded-sm" value={form.airline_name} onChange={(e) => setForm({ ...form, airline_name: e.target.value })} data-testid="dispatch-job-airline" /></div>
            )}
          </div>
          <DialogFooter><Button className="rounded-sm" onClick={createJob} disabled={busy} data-testid="dispatch-job-submit">{busy ? "Creating…" : "Create Dispatch Job"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!holding} onOpenChange={(o) => !o && setHolding(null)}>
        <DialogContent data-testid="hold-dispatch-dialog">
          <DialogHeader><DialogTitle>Stop dispatch {holding?.dispatch_number}?</DialogTitle></DialogHeader>
          <div className="space-y-3 text-sm">
            <p className="text-muted-foreground">The dispatch team will get an immediate <span className="font-semibold text-red-700">STOP alert</span> and will not be able to mark this job as dispatched until you release it. You can also cancel the job entirely by deleting it.</p>
            <div><Label className="text-xs uppercase tracking-wide">Reason (shown to dispatch team)</Label><Input className="mt-1.5 rounded-sm" value={holdReason} onChange={(e) => setHoldReason(e.target.value)} placeholder="e.g. client changed address, payment pending…" data-testid="hold-reason-input" /></div>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setHolding(null)} data-testid="hold-dispatch-cancel">Back</Button>
            <Button variant="destructive" className="rounded-sm" onClick={confirmHold} disabled={busyHold} data-testid="hold-dispatch-confirm"><HandPalm size={16} className="mr-2" /> {busyHold ? "Stopping…" : "Stop dispatch"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent data-testid="delete-dispatch-dialog">
          <DialogHeader><DialogTitle>Delete dispatch job?</DialogTitle></DialogHeader>
          <div className="space-y-2 text-sm text-muted-foreground">
            <p>This will permanently delete dispatch <span className="font-semibold text-foreground">{deleting?.dispatch_number}</span>{deleting?.order_number ? <> for order <span className="font-semibold text-foreground">{deleting.order_number}</span></> : null}.</p>
            <p>The linked order{deleting?.order_ids?.length > 1 ? "s" : ""} will be released so {deleting?.order_ids?.length > 1 ? "they" : "it"} can be sent to dispatch again. Any dispatch invoices raised on this job will also be removed.</p>
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" className="rounded-sm" onClick={() => setDeleting(null)} data-testid="delete-dispatch-cancel">Cancel</Button>
            <Button variant="destructive" className="rounded-sm" onClick={confirmDelete} disabled={busyDel} data-testid="delete-dispatch-confirm">{busyDel ? "Deleting…" : "Delete dispatch"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
